import { createSelector } from "reselect";

import { ThousandSeparatorFormatNames } from "../../../../constants/dateFormats";
import { immutableRecords } from "../../../../types";
import floatToInt from "../../../../utils/floatToInt";
import { isGregorianCalendarFromCalendar } from "../gregorianCalendar";
import {
  getEraFromCalendar as getEra,
  getErasFromCalendar as getEras,
  getHoursInDayFromCalendar as getHoursInDay,
  getMonthFromCalendar as getMonth,
  getMonthsFromCalendar as getMonths,
  getWeekdayIndexAtZeroFromCalendar as getWeekdayIndexAtZero,
  getWeekdaysFromCalendar as getWeekdays,
  isLeapYearFromCalendar as isLeapYearInEra
} from "../selectors";

// ===================================================
// Exported Functions
// ===================================================

export const createImmutableDate = (era, year, month, day, hour, minute, second, timestamp, weekday, precision) => {
  return immutableRecords.DateRecord({
    era,
    year,
    month,
    day,
    hour,
    minute,
    second,
    timestamp,
    precision,
    weekday
  });
};

export const calculateTimestampForDate = (calendar, era, year, month, day, hour, minute, second) => {
  const { daysInEra, hoursInDay } = calendarPrecalculations(calendar);

  const eras = getEras(calendar);
  const eraCount = eras.count();
  const eraObj = getEra(calendar, era);
  const isEraBackwards = eraObj.get("isBackwards");
  // Work out how many days we are since the origin "0"
  // note that "0" is always considered to be 1/1/1 00:00:00 of the final era.

  let daysSinceOrigin = 0;
  if (era === eraCount - 1) {
    // Easy case - we are in the final era, so just work out the days in this era.
    daysSinceOrigin = calculateDaysSinceEraStartForDateComponents(
      calendar,
      era,
      year,
      month,
      day,
      hour,
      minute,
      second
    );
  } else {
    // Otherwise, we have a negative days value.
    // Add in all eras between last era and current era.
    for (let i = eraCount - 2; i > era; i--) {
      daysSinceOrigin -= daysInEra[i];
    }
    if (isEraBackwards) {
      daysSinceOrigin -= calculateDaysSinceEraStartForDateComponents(
        calendar,
        era,
        year,
        month,
        day,
        hour,
        minute,
        second
      );
    } else {
      daysSinceOrigin -= daysInEra[era];
      daysSinceOrigin += calculateDaysSinceEraStartForDateComponents(
        calendar,
        era,
        year,
        month,
        day,
        hour,
        minute,
        second
      );
    }
  }

  //let hToAdd, mToAdd, sToAdd;

  // Convert days to seconds since origin.
  let timestamp = daysSinceOrigin * hoursInDay * 3600;

  // Then add how far into the day we are...
  if (isEraBackwards) {
    let hoursToAdd = hoursInDay - hour;
    if (minute !== 0 || second !== 0) {
      hoursToAdd -= 1;
    }
    let minutesToAdd = 60 - minute;
    if (second !== 0) {
      minutesToAdd -= 1;
    }
    let secondsToAdd = 60 - second;
    if (hoursToAdd === hoursInDay) {
      hoursToAdd = 0;
    }
    if (minutesToAdd === 60) {
      minutesToAdd = 0;
    }
    if (secondsToAdd === 60) {
      secondsToAdd = 0;
    }
    timestamp -= hoursToAdd * 3600 + minutesToAdd * 60 + secondsToAdd;
  } else {
    timestamp += hour * 3600 + minute * 60 + second;
  }
  return timestamp;
};

export const calculateComponentsForTimestamp = (calendar, timestamp) => {
  const numEras = getEras(calendar).count();
  // special case - timestamp 0 is very easy!
  if (timestamp === 0) {
    return {
      era: numEras - 1,
      year: 1,
      month: 1,
      day: 1,
      hour: 0,
      minute: 0,
      second: 0
    };
  }
  if (timestamp > 0) {
    return calculateComponentsForPositiveTimestamp(calendar, timestamp);
  }
  return calculateComponentsForNegativeTimestamp(calendar, timestamp);
};

export const calculateWeekdayForTimestamp = (calendar, timestamp) => {
  const precalculations = calendarPrecalculations(calendar);
  const secondsInDay = precalculations.secondsInDay;

  const daysFromZero = floatToInt(timestamp / secondsInDay);
  const dayCount = getWeekdays(calendar).count();

  if (timestamp >= 0) {
    return (getWeekdayIndexAtZero(calendar) + daysFromZero) % dayCount;
  } else {
    let days = Math.abs(daysFromZero % dayCount);
    let weekdayIndex = getWeekdayIndexAtZero(calendar) - days;

    if (timestamp % secondsInDay !== 0) {
      weekdayIndex -= 1;
    }
    if (weekdayIndex < 0) {
      weekdayIndex += dayCount;
    }
    return weekdayIndex;
  }
};

export const addYearsToDate = (calendar, era, year, yearsToAdd) => {
  // In-place add the years into this date.
  let val = yearsToAdd;
  // localEra,
  // currEra;
  while (val !== 0) {
    let eraObj = getEra(calendar, era);
    if (eraObj.get("isBackwards")) {
      // counts backwards, like BC dates...
      // so we subtract to go forwards (goes up if years is negative)...
      year -= val;
      if (year <= 0) {
        // we have gone below zero, which means we are moving into the next era
        // like moving from BC to AD...
        era++;
        eraObj = getEra(calendar, era);
        if (eraObj.get("isBackwards")) {
          // the next era ahead of this one is counting backwards.
          val = -year;
          year = eraObj.get("duration");
        } else {
          // the next era era is a forward counting one...
          // so set the current year to be 1
          val = -year;
          year = 1;
        }
      } else if (year > eraObj.get("duration")) {
        // we have gone backwards past the maximum range of this era.
        era--;
        let currEraObj = eraObj;
        eraObj = getEra(calendar, era);
        if (eraObj.get("isBackwards")) {
          // the era behind this one is the backwards infinity...
          // so start at 0, and leave the yearOfEra in negatives...
          val = currEraObj.get("duration") - year;
          year = 1;
        } else {
          // the previous era is a forward counting one...
          // so leave years negative, but set the current year to be
          // the end of the previous era
          val = currEraObj.get("duration") - year;
          year = eraObj.get("duration");
        }
      } else {
        return { era, year };
      }
    } else {
      // counts forwards
      year += val;
      if (year > eraObj.get("duration")) {
        era++;
        let currEraObj = eraObj;
        eraObj = getEra(calendar, era);
        if (eraObj.get("isBackwards")) {
          // the next era ahead of this one is counting backwards.
          val = year - currEraObj.get("duration") - 1;
          year = eraObj.get("duration");
        } else {
          // the next era era is a forward counting one...
          // so set the current year to be 0
          val = year - currEraObj.get("duration");
          year = 0;
        }
      } else if (year <= 0) {
        era--;
        eraObj = getEra(calendar, era);
        if (eraObj.get("isBackwards")) {
          // the era behind this one is backwards...
          // so start at 0, and leave the yearOfEra in negatives...
          val = year;
          year = 1;
        } else {
          // the previous era is a forward counting one...
          // so leave years negative, but set the current year to be
          // the end of the previous era
          val = year;
          year = eraObj.get("duration");
        }
      } else {
        return { era, year };
      }
    }
  }
  return { era, year };
};

/**
 *
 * @param {CalendarRecord} calendar
 * @param {number} era index of the era in the eras list
 * @param {number} year
 * @return {number}
 */
export const getFirstMonthOfYear = (calendar, era, year) => {
  const monthCount = getMonths(calendar).count();
  const durationLookup = isLeapYearInEra(calendar, era, year) ? "leapDuration" : "normalDuration";
  for (let i = 1; i <= monthCount; i++) {
    if (getMonth(calendar, i).get(durationLookup) > 0) {
      return i;
    }
  }
  // If none of the months have a date in that year, this represents a bug.
  // eslint-disable-next-line no-console
  console.log(`getFirstMonthOfYear(): error, in year ${year} of era ${era}, no months have any duration`);
  return 1;
};

export const calculateMonthsSinceEraStart = (calendar, date) => {
  const monthCount = getMonths(calendar).count();
  if (calendar.getIn(["eras", date.era, "isBackwards"])) {
    return (date.year - 1) * monthCount + monthCount - date.month;
  } else {
    return (date.year - 1) * monthCount + (date.month - 1);
  }
};

// ===================================================
// Internal Functions
// ===================================================

export const getMaximumDaysInAnyMonth = months =>
  months.reduce((result, m) => Math.max(m.normalDuration, m.leapDuration, result), 0);

// Note: this function is protected by createSelector, so it should only be called if the calendar itself
// changes...
export const calendarPrecalculations = createSelector(
  // select the calendar from what is passed in
  calendar => calendar,
  // And this is the function that calculates what we need...
  calendar => {
    const eras = getEras(calendar);
    const months = getMonths(calendar);
    const weekdays = getWeekdays(calendar);
    const hoursInDay = getHoursInDay(calendar);

    const maximumDaysInAnyMonth = getMaximumDaysInAnyMonth(months);
    let daysInNormalYear = 0;
    let daysInLeapYear = 0;
    let monthStartsInNormalYear = [];
    let monthStartsInLeapYear = [];

    months.forEach(m => {
      monthStartsInNormalYear.push(daysInNormalYear);
      monthStartsInLeapYear.push(daysInLeapYear);
      daysInNormalYear += m.normalDuration;
      daysInLeapYear += m.leapDuration;
    });

    const extraDaysInLeapYear = daysInLeapYear - daysInNormalYear;

    const leapCycles = calendar.leapCycles.toArray();
    const numLeapCycles = leapCycles.length;

    let daysInCycle0 = 0;
    let daysInCycle1 = 0;
    let daysInCycle2 = 0;
    let daysInLeapCycles = [];
    if (numLeapCycles >= 1) {
      // first leap cycle: every N years
      daysInCycle0 = leapCycles[0] * daysInNormalYear + extraDaysInLeapYear;
      daysInLeapCycles.push(daysInCycle0);
    }
    if (numLeapCycles >= 2) {
      // 2nd leap cycle: repeat previous pattern, and subtract extraDaysInLeapYear
      const repeats = leapCycles[1] / leapCycles[0];
      daysInCycle1 = repeats * daysInCycle0 - extraDaysInLeapYear;
      daysInLeapCycles.push(daysInCycle1);
    }
    if (numLeapCycles >= 3) {
      // 3rd leap cycle: repeat previous pattern, and add extraDaysInLeapYear
      const repeats = leapCycles[2] / leapCycles[1];
      daysInCycle2 = repeats * daysInCycle1 + extraDaysInLeapYear;
      daysInLeapCycles.push(daysInCycle2);
    }

    const secondsInDay = hoursInDay * 3600;
    const secondsInWeek = secondsInDay * weekdays.count();
    const avgDaysInYear = floatToInt(daysInLeapCycles[daysInLeapCycles.length - 1] / leapCycles[leapCycles.length - 1]);
    const avgDaysInMonth = floatToInt(avgDaysInYear / months.count());
    const avgSecondsInYear = avgDaysInYear * secondsInDay;
    const avgSecondsInMonth = avgDaysInMonth * secondsInDay;

    let daysInLeapOffsetPeriod = [];
    let daysInEra = [];
    let secondsInEra = [];

    const calcDaysInLeapOffsetPeriod = offset => (offset ? (offset - 1) * daysInNormalYear + daysInLeapYear : 0);

    const calcDaysInLeapingEra = (numYears, offset) => {
      // offset years are just normal years
      let totalYears = calcDaysInLeapOffsetPeriod(offset);
      // take them off the remaining years
      let remainingYears = numYears - offset;
      // For each cycle (from largest down), work out how many complete cycles
      // there are in this era.
      for (let c = numLeapCycles - 1; c >= 0; c--) {
        const cYears = leapCycles[c];
        const cDays = daysInLeapCycles[c];
        const fullCycles = floatToInt(remainingYears / cYears);
        totalYears += fullCycles * cDays;
        remainingYears -= fullCycles * cYears;
      }
      // Finally, there may be some remainder left over, which are also normal years?
      totalYears += remainingYears * daysInNormalYear;
      return totalYears;
    };

    eras.forEach(e => {
      daysInLeapOffsetPeriod.push(calcDaysInLeapOffsetPeriod(e.leapOffset));
      const days = e.hasLeapYears ? calcDaysInLeapingEra(e.duration, e.leapOffset) : e.duration * daysInNormalYear;
      daysInEra.push(days);
      secondsInEra.push(days * secondsInDay);
    });

    // Finally, is it a normal calendar
    const isGregorianCalendar = isGregorianCalendarFromCalendar(calendar);

    // Return all the values we calculated.
    return {
      maximumDaysInAnyMonth,
      daysInNormalYear,
      daysInLeapYear,
      monthStartsInLeapYear,
      monthStartsInNormalYear,
      numLeapCycles,
      leapCycles,
      daysInLeapCycles,
      extraDaysInLeapYear,
      hoursInDay,
      secondsInDay,
      secondsInWeek,
      avgDaysInYear,
      avgDaysInMonth,
      avgSecondsInYear,
      avgSecondsInMonth,
      daysInLeapOffsetPeriod,
      daysInEra,
      secondsInEra,
      isGregorianCalendar
    };
  }
);

// Note: this function is protected by createSelector, so it should only be called if the calendar itself
// changes...
export const calendarRegularExpressions = createSelector(
  // select the calendar from what is passed in
  calendar => calendar,
  // And this is the function that calculates what we need...
  calendar => {
    const eras = getEras(calendar);
    const months = getMonths(calendar);
    const weekdays = getWeekdays(calendar);

    /* eslint-disable no-useless-concat */
    const pseudoBoundaryStart = "^(.*[\\s\\,\\-\\/]+)?";
    const pseudoBoundaryEnd = "([\\s\\,\\-\\/]+.*)?$";

    // Month Regular Expressions
    let allMonthsString = pseudoBoundaryStart + "(",
      longMonthsString = pseudoBoundaryStart + "(",
      shortMonthsString = pseudoBoundaryStart + "(";

    months.forEach(m => {
      allMonthsString += "(" + m.name + ")|(" + m.shortName + ")|";
      longMonthsString += "(" + m.name + ")|";
      shortMonthsString += "(" + m.shortName + ")|";
    });
    allMonthsString = allMonthsString.substr(0, allMonthsString.length - 1) + ")" + pseudoBoundaryEnd;
    longMonthsString = longMonthsString.substr(0, longMonthsString.length - 1) + ")" + pseudoBoundaryEnd;
    shortMonthsString = shortMonthsString.substr(0, shortMonthsString.length - 1) + ")" + pseudoBoundaryEnd;

    const monthNameRegExp = new RegExp(allMonthsString, "i");
    const longMonthNameRegExp = new RegExp(longMonthsString, "i");
    const shortMonthNameRegExp = new RegExp(shortMonthsString, "i");
    const monthNameRemainderRegExp =
      calendar && calendar.thousandSeparatorFormat === ThousandSeparatorFormatNames.Period
        ? new RegExp("\\b((\\d{1,3}(.\\d{3})*)|(\\d+))([\\s,/-]+((\\d{1,3}(.\\d{3})*)|(\\d+)))?\\b")
        : new RegExp("\\b((\\d{1,3}(,\\d{3})*)|(\\d+))([\\s,/-]+((\\d{1,3}(,\\d{3})*)|(\\d+)))?\\b");

    // Era Regular Expressions
    let allErasStringWithDigit = pseudoBoundaryStart + "(\\d*)(",
      allErasString = pseudoBoundaryStart + "(",
      longErasString = pseudoBoundaryStart + "(",
      shortErasString = pseudoBoundaryStart + "(";

    eras.forEach(e => {
      allErasString += "(" + e.name + ")|(" + e.shortName + ")|";
      allErasStringWithDigit += "(" + e.name + ")|(" + e.shortName + ")|";
      longErasString += "(" + e.name + ")|";
      shortErasString += "(" + e.shortName + ")|";
    });

    allErasString = allErasString.substr(0, allErasString.length - 1) + ")\\s*$";
    allErasStringWithDigit = allErasStringWithDigit.substr(0, allErasStringWithDigit.length - 1) + ")\\s*(.*)$";
    longErasString = longErasString.substr(0, longErasString.length - 1) + ")\\s*$";
    shortErasString = shortErasString.substr(0, shortErasString.length - 1) + ")\\s*$";

    const eraNameWithDigitRegExp = new RegExp(allErasStringWithDigit, "i");
    const eraNameRegExp = new RegExp(allErasString, "i");
    const longEraNameRegExp = new RegExp(longErasString, "i");
    const shortEraNameRegExp = new RegExp(shortErasString, "i");

    // Weekday Regular Expressions
    let allWeekdaysString = pseudoBoundaryStart + "(",
      longWeekdaysString = pseudoBoundaryStart + "(",
      shortWeekdaysString = pseudoBoundaryStart + "(";

    weekdays.forEach(w => {
      allWeekdaysString += "(" + w.name + ")|(" + w.shortName + ")|";
      longWeekdaysString += "(" + w.name + ")|";
      shortWeekdaysString += "(" + w.shortName + ")|";
    });
    allWeekdaysString = allWeekdaysString.substr(0, allWeekdaysString.length - 1) + ")" + pseudoBoundaryEnd;
    longWeekdaysString = longWeekdaysString.substr(0, longWeekdaysString.length - 1) + ")" + pseudoBoundaryEnd;
    shortWeekdaysString = shortWeekdaysString.substr(0, shortWeekdaysString.length - 1) + ")" + pseudoBoundaryEnd;

    const weekdayNameRegExp = new RegExp(allWeekdaysString, "i");
    const longWeekdayNameRegExp = new RegExp(longWeekdaysString, "i");
    const shortWeekdayNameRegExp = new RegExp(shortWeekdaysString, "i");

    // Time Regular Expressions
    let timeString = "\\b" + "(\\d{1,2})" + "([.:]([0-5][0-9])" + "(:([0-5][0-9]))?" + ")?" + "\\s?([ap][m]?)" + "\\b";
    const timeRegExp = new RegExp(timeString, "i");

    timeString = "\\b" + "(\\d{1,2})" + ":([0-5][0-9])" + "(:([0-5][0-9]))?(\\.\\d+)?";
    const timeRegExp2 = new RegExp(timeString);

    timeString = "\\b" + "(\\d{1,2})" + "([0-5][0-9])" + "\\s?hr?s?" + "\\b";
    const timeRegExp3 = new RegExp(timeString, "i");

    timeString = "^\\s*([-+])?" + "\\s*" + "(\\d+)" + "((:([0-5][0-9]))" + "(:([0-5][0-9]))?)?" + "\\s*$";
    const timeRegExp4 = new RegExp(timeString);

    // Date Regular Expressions
    const formalDateRegExp = new RegExp("\\b(\\d+)[/.-](\\d+)[/.-](\\d+)\\b");
    const monthDateRegExp = new RegExp("\\b(\\d+)[/.-](\\d+)\\b");
    const yearMonthRegExp = new RegExp("\\b(\\d+)-(\\d+)\\b");
    const matchLastWordRegExp = new RegExp("\\b([a-zA-z]+)$");
    const matchFirstWordRegExp = new RegExp("^([a-zA-z]+)");
    const remainingErrorContent = new RegExp("(\\S)+");

    // Years
    const yearRegExp =
      calendar && calendar.thousandSeparatorFormat === ThousandSeparatorFormatNames.Period
        ? new RegExp("\\b((\\d{1,3}(.\\d{3})*)|(\\d+))\\b", "i")
        : new RegExp("\\b((\\d{1,3}(,\\d{3})*)|(\\d+))\\b", "i");
    const largeYearRegExp =
      calendar && calendar.thousandSeparatorFormat === ThousandSeparatorFormatNames.Period
        ? new RegExp("\\b((((\\d{1,3}(.\\d{3})*)(,\\d+)?)|(\\d+))(,\\d+)?)\\s?(billion|million)\\b", "i")
        : new RegExp("\\b((((\\d{1,3}(,\\d{3})*)(.\\d+)?)|(\\d+))(.\\d+)?)\\s?(billion|million)\\b", "i");

    // Floating Weeks
    const weekString = "(\\b(w(eek|k)?)" + "\\s*)" + "(-?\\d+)";
    const weekRegExp = new RegExp(weekString, "i");

    // Floating Days
    const dayString = "(\\b(d(ay)?)" + "\\s*)" + "(-?\\d+)" + "\\b";
    const dayRegExp = new RegExp(dayString, "i");
    /* eslint-enable no-useless-concat */

    // TODO: Confirm all of these regular expressions are used elsewhere in the code
    // and delete any that are no longer required.

    // Return all the values we calculated.
    return {
      monthNameRegExp,
      longMonthNameRegExp,
      shortMonthNameRegExp,
      monthNameRemainderRegExp,
      eraNameWithDigitRegExp,
      eraNameRegExp,
      longEraNameRegExp,
      shortEraNameRegExp,
      weekdayNameRegExp,
      longWeekdayNameRegExp,
      shortWeekdayNameRegExp,
      timeRegExp,
      timeRegExp2,
      timeRegExp3,
      timeRegExp4,
      formalDateRegExp,
      monthDateRegExp,
      yearMonthRegExp,
      matchLastWordRegExp,
      matchFirstWordRegExp,
      remainingErrorContent,
      yearRegExp,
      largeYearRegExp,
      weekRegExp,
      dayRegExp
    };
  }
);

const calcNumLeapYearsInYears = (leapCyclesArr, leapOffset, years) => {
  if (leapOffset) {
    if (years < leapOffset) {
      return 0;
    }
    if (years === leapOffset) {
      return 1;
    }
  }
  // Otherwise, we are above offset, so look at each block.
  let numLeapYears = 0;
  for (let c = leapCyclesArr.length - 1; c >= 0; c--) {
    const adjust = floatToInt((years - leapOffset) / leapCyclesArr[c]);
    numLeapYears += c % 2 === 0 ? adjust : -adjust;
  }
  // If we have an offset, add 1
  return leapOffset > 0 ? numLeapYears + 1 : numLeapYears;
};

export const calculateDaysSinceEraStartForDateComponents = (calendar, era, year, month, day, hour, minute, second) => {
  const { daysInNormalYear, daysInLeapYear, leapCycles } = calendarPrecalculations(calendar);
  const eraObj = getEra(calendar, era);
  const eraHasLeapYears = eraObj.get("hasLeapYears");
  const eraIsBackwards = eraObj.get("isBackwards");
  const monthCount = getMonths(calendar).count();

  let yearsIntoEra = year - 1,
    daysSinceEraStart = daysInNormalYear * yearsIntoEra,
    numLeapYears;

  if (eraHasLeapYears) {
    numLeapYears = calcNumLeapYearsInYears(leapCycles, eraObj.leapOffset, yearsIntoEra);
    daysSinceEraStart += numLeapYears * (daysInLeapYear - daysInNormalYear);
  }

  // do i need to lookup leap or normal duration?
  const durationLookup = isLeapYearInEra(calendar, era, year) ? "leapDuration" : "normalDuration";

  if (eraIsBackwards) {
    for (let i = monthCount; i > month; i--) {
      daysSinceEraStart += getMonth(calendar, i).get(durationLookup);
    }
    daysSinceEraStart += getMonth(calendar, month).get(durationLookup) - day;
    if (hour === 0 && minute === 0 && second === 0) {
      daysSinceEraStart++;
    }
  } else {
    for (let i = 1; i <= monthCount; i++) {
      if (i === month) {
        break;
      }
      daysSinceEraStart += getMonth(calendar, i).get(durationLookup);
    }
    daysSinceEraStart += day - 1;
  }

  return daysSinceEraStart;
};

// If we are N days into a leap year, what is the day and month?
const calculateMonthAndDayForDaysIntoLeapYear = (calendar, daysIntoYear) => {
  const { monthStartsInLeapYear } = calendarPrecalculations(calendar);
  const monthCount = monthStartsInLeapYear.length;
  for (let i = monthCount - 1; i >= 0; i--) {
    if (daysIntoYear >= monthStartsInLeapYear[i]) {
      return { month: i + 1, day: 1 + daysIntoYear - monthStartsInLeapYear[i] };
    }
  }
  throw "daysIntoYear is less than zero (calculateMonthAndDayForDaysIntoLeapYear)";
};

// If we are N days into a regular year, what is the day and month?
const calculateMonthAndDayForDaysIntoNormalYear = (calendar, daysIntoYear) => {
  const { monthStartsInNormalYear } = calendarPrecalculations(calendar);
  const monthCount = monthStartsInNormalYear.length;
  for (let i = monthCount - 1; i >= 0; i--) {
    if (daysIntoYear >= monthStartsInNormalYear[i]) {
      return {
        month: i + 1,
        day: 1 + daysIntoYear - monthStartsInNormalYear[i]
      };
    }
  }
  throw "daysIntoYear is less than zero (calculateMonthAndDayForDaysIntoNormalYear)";
};

// If we are N days into a regular year in an era that runs backwards, what is the day and month?
const calculateMonthAndDayForDaysIntoNegativeNormalYear = (calendar, daysIntoYear) => {
  const { daysInNormalYear, monthStartsInNormalYear } = calendarPrecalculations(calendar);
  const monthCount = monthStartsInNormalYear.length;

  for (let i = monthCount - 1; i >= 0; i--) {
    if (daysIntoYear <= daysInNormalYear - monthStartsInNormalYear[i]) {
      return {
        month: i + 1,
        day: daysInNormalYear - monthStartsInNormalYear[i] - daysIntoYear + 1
      };
    }
  }
};

const calculateMonthAndDayForDaysIntoNegativeLeapYear = (calendar, daysIntoYear) => {
  const { daysInLeapYear, monthStartsInLeapYear } = calendarPrecalculations(calendar);
  const monthCount = monthStartsInLeapYear.length;

  for (let i = monthCount - 1; i >= 0; i--) {
    if (daysIntoYear <= daysInLeapYear - monthStartsInLeapYear[i]) {
      return {
        month: i + 1,
        day: daysInLeapYear - monthStartsInLeapYear[i] - daysIntoYear + 1
      };
    }
  }
};

// These are special functions that allow us to calculate dates very fast for the Gregorian calendar
// due to a special maths trick
// Source: https://alcor.concordia.ca/~gpkatch/gdate-algorithm.html
// Algorithm Description: https://alcor.concordia.ca/~gpkatch/gdate-method.html
export const toDaysSinceCalcZero = (leap, year, month, day) => {
  let m = month,
    y = year,
    d = day;

  if (leap) {
    m = (m + 9) % 12;
    y = y - floatToInt(m / 10);
    return (
      y * 365 + floatToInt(y / 4) - floatToInt(y / 100) + floatToInt(y / 400) + floatToInt((m * 306 + 5) / 10) + (d - 1)
    );
  } else {
    m = (m + 9) % 12;
    y = y + floatToInt(m / 10);
    return y * 365 - floatToInt((m * 306 + 5) / 10) + (d - 1);
  }
};
export const fromDaysSinceCalcZero = (leap, value) => {
  if (leap) {
    let y = floatToInt((10000 * value + 14780) / 3652425);
    let rem = value - (y * 365 + floatToInt(y / 4) - floatToInt(y / 100) + floatToInt(y / 400));
    if (rem < 0) {
      y--;
      rem = value - (y * 365 + floatToInt(y / 4) - floatToInt(y / 100) + floatToInt(y / 400));
    }
    let mi = floatToInt((52 + 100 * rem) / 3060);
    return {
      year: y + floatToInt((mi + 2) / 12),
      month: floatToInt((mi + 2) % 12) + 1,
      day: rem - floatToInt((mi * 306 + 5) / 10) + 1
    };
  } else {
    let y = value / 365;
    let ddd = y * 365 - value;
    if (ddd < 0) {
      y++;
      ddd = value - y * 365;
    }
    let mi = floatToInt((52 + 100 * ddd) / 3060);
    return {
      year: y - floatToInt((mi + 2) / 12),
      month: floatToInt((mi + 2) % 12) + 1,
      day: ddd - floatToInt((mi * 306 + 5) / 10) + 1
    };
  }
};

const calcLeapingYearsInDays = (precalculations, numDays, leapOffset, daysInLeapOffset) => {
  const { leapCycles, daysInLeapCycles, daysInNormalYear, daysInLeapYear } = precalculations;
  let years = 0;
  let remainingDays = numDays;
  // The date falls into the leapOffset period
  // The last year of the leapOffset period is a leap year
  // So see how many whole normal years we can fit in first before we hit that
  // final leapYear
  if (numDays < daysInLeapOffset) {
    // Otherwise, we need to work out which year it falls in within the offset period
    while (remainingDays > daysInLeapYear) {
      years++;
      remainingDays -= daysInNormalYear;
    }
    return { years, remainingDays };
  }

  // Otherwise, we are beyond the offset period

  years = leapOffset;
  remainingDays -= daysInLeapOffset;

  for (let c = leapCycles.length - 1; c >= 0; c--) {
    const cYears = leapCycles[c];
    const cDays = daysInLeapCycles[c];
    const fullCycles = floatToInt(remainingDays / cDays);
    const ratio = c < leapCycles.length - 1 ? leapCycles[c + 1] / cYears : null;

    // We need to be careful around the value at index 1, as it may inadvertently divide evenly
    // when we don't want it to (e.g. days in 400 days fails, but days in 4 * 100 days succeeds)
    // So if we are at an odd index, and it is not the highest value, we can't allow through the
    // full ratio if it hits on that magic number.
    if (!ratio || fullCycles < ratio || c % 2 === 0) {
      years += fullCycles * cYears;
      remainingDays -= fullCycles * cDays;
    } else {
      years += (fullCycles - 1) * cYears;
      remainingDays -= (fullCycles - 1) * cDays;
    }
  }

  // Now we just need to deal with whatever is left
  // Again, we don't want to allow it to be perfectly even.
  // So we will add at most "3" years here under normal circumstances (where 3 is based on a leap cycle of 4)
  //
  const remainingYears = floatToInt(remainingDays / daysInNormalYear);
  const finalYears = Math.min(leapCycles[0] - 1, remainingYears);
  years += finalYears;
  remainingDays -= finalYears * daysInNormalYear;
  return { years, remainingDays };
};

const calculateComponentsForPositiveTimestamp = (calendar, timestamp) => {
  const precalculations = calendarPrecalculations(calendar);
  const numEras = getEras(calendar).count();
  //const hoursInDay = getHoursInDay("calendar");
  const secondsInDay = precalculations.secondsInDay;

  let era = numEras - 1;
  let year = 1;
  let month = 1;
  let day = 1;

  let daysSinceStart = floatToInt(timestamp / secondsInDay);

  const calcNeedsLeapYears =
    getEra(calendar, era).get("hasLeapYears") && precalculations.daysInNormalYear !== precalculations.daysInLeapYear;
  const leapOffset = getEra(calendar, era).get("leapOffset");
  const daysInLeapOffset = precalculations.daysInLeapOffsetPeriod[era];

  if (!calcNeedsLeapYears) {
    year += floatToInt(daysSinceStart / precalculations.daysInNormalYear);
    const remainingDays = daysSinceStart % precalculations.daysInNormalYear;
    if (remainingDays > 0) {
      const isLeapYear = isLeapYearInEra(calendar, era, year);
      if (isLeapYear) {
        ({ month, day } = calculateMonthAndDayForDaysIntoLeapYear(calendar, remainingDays));
      } else {
        ({ month, day } = calculateMonthAndDayForDaysIntoNormalYear(calendar, remainingDays));
      }
    }
  } else if (precalculations.isGregorianCalendar) {
    // deal with the leap years.
    // do the conversion-style calculation.
    const val = toDaysSinceCalcZero(true, year, month, day) + daysSinceStart;
    ({ year, month, day } = fromDaysSinceCalcZero(true, val));
  } else {
    // we have to worry about leap years now...
    // so we have to add in different blocks at a time.
    let { years: addedYears, remainingDays } = calcLeapingYearsInDays(
      precalculations,
      daysSinceStart,
      leapOffset,
      daysInLeapOffset
    );

    year += addedYears;

    const isLeapYear = isLeapYearInEra(calendar, era, year);

    if (isLeapYear) {
      ({ month, day } = calculateMonthAndDayForDaysIntoLeapYear(calendar, remainingDays));
    } else {
      ({ month, day } = calculateMonthAndDayForDaysIntoNormalYear(calendar, remainingDays));
    }
  }

  let remainingSeconds = timestamp % secondsInDay;
  const hour = floatToInt(remainingSeconds / 3600);
  remainingSeconds %= 3600;
  const minute = floatToInt(remainingSeconds / 60);
  const second = remainingSeconds % 60;

  return { era, year, month, day, hour, minute, second };
};

/**
 *
 * @param {CalendarRecord} calendar
 * @param {number} remainingTS Positive number
 * @param {number} era the index of the era in the eras list
 */
const calculateComponentsForNegativeTimestampBackwardsEra = (calendar, remainingTS, era) => {
  const precalculations = calendarPrecalculations(calendar);
  const secondsInDay = precalculations.secondsInDay;

  // Work out which era we are in...
  let year = 1;
  let month = 1;
  let day = 1;
  let hour = 0;
  let minute = 0;
  let second = 0;

  const daysInLeapOffset = precalculations.daysInLeapOffsetPeriod[era];
  let eraObj = getEra(calendar, era);

  if (!eraObj.get("hasLeapYears")) {
    let daysSinceStart = floatToInt(remainingTS / secondsInDay);
    let yearsSinceStart = floatToInt(daysSinceStart / precalculations.daysInNormalYear);
    year = yearsSinceStart;
    remainingTS -= yearsSinceStart * precalculations.daysInNormalYear * secondsInDay;
    if (remainingTS === 0) {
      month = 1;
      day = 1;
    } else {
      year += 1;
      let remainingDays = floatToInt(remainingTS / secondsInDay);
      remainingTS -= remainingDays * secondsInDay;
      ({ month, day } = calculateMonthAndDayForDaysIntoNegativeNormalYear(calendar, remainingDays));
    }
  } else {
    let daysSinceStart = floatToInt(remainingTS / secondsInDay);

    let { years: yearsSinceStart, remainingDays } = calcLeapingYearsInDays(
      precalculations,
      daysSinceStart,
      eraObj.leapOffset,
      daysInLeapOffset
    );

    const subtractedDays = daysSinceStart - remainingDays;
    const subtractedSeconds = subtractedDays * secondsInDay;

    daysSinceStart = remainingDays;

    year = yearsSinceStart;
    remainingTS -= subtractedSeconds;

    if (remainingTS === 0) {
      month = 1;
      day = 1;
    } else {
      year += 1;
      let remainingDays = floatToInt(remainingTS / secondsInDay);
      let isLeapYear = isLeapYearInEra(calendar, era, year);

      if (isLeapYear) {
        ({ month, day } = calculateMonthAndDayForDaysIntoNegativeLeapYear(calendar, remainingDays));
      } else {
        ({ month, day } = calculateMonthAndDayForDaysIntoNegativeNormalYear(calendar, remainingDays));
      }
      const subtracted = remainingDays * secondsInDay;
      remainingTS -= subtracted;
    }
  }

  if (remainingTS === 0) {
    hour = 0;
    minute = 0;
    second = 0;
  } else {
    day -= 1;
    if (day === 0) {
      month -= 1;
      if (month === 0) {
        year -= 1;
        if (year === 0) {
          era--;
          if (getEra(calendar, era).get("isBackwards")) {
            year = 1;
          } else {
            year = getEra(calendar, era).get("duration");
          }
        }
        month = getMonths(calendar).count();
      }
      const durationLookup = isLeapYearInEra(calendar, era, year) ? "leapDuration" : "normalDuration";
      day = getMonth(calendar, month).get(durationLookup);
    }
    hour = precalculations.hoursInDay - floatToInt(remainingTS / 3600) - 1;
    remainingTS = remainingTS % 3600;
    if (remainingTS === 0) {
      minute = 0;
      second = 0;
      hour++;
    } else {
      minute = 60 - floatToInt(remainingTS / 60) - 1;
      remainingTS = remainingTS % 60;
      if (remainingTS === 0) {
        second = 0;
        minute++;
      } else {
        second = 60 - remainingTS;
      }
    }
  }
  return { era, year, month, day, hour, minute, second };
};

const calculateComponentsForNegativeTimestampForwardsEra = (calendar, remainingTS, era) => {
  const precalculations = calendarPrecalculations(calendar);
  const secondsInDay = precalculations.secondsInDay;
  // Work out which era we are in...
  let year = 1;
  let month = 1;
  let day = 1;
  let hour = 0;
  let minute = 0;
  let second = 0;

  let eraObj = getEra(calendar, era);
  let secondsInEra = precalculations.secondsInEra[era];
  let daysInEra = precalculations.daysInEra[era];
  const daysInLeapOffset = precalculations.daysInLeapOffsetPeriod[era];

  let daysSinceStart = daysInEra - floatToInt(remainingTS / secondsInDay);
  if (remainingTS % secondsInDay !== 0) {
    daysSinceStart--;
  }
  if (!eraObj.get("hasLeapYears")) {
    let yearsSinceStart = floatToInt(daysSinceStart / precalculations.daysInNormalYear);
    year = yearsSinceStart + 1;
    let subtracted = yearsSinceStart * precalculations.daysInNormalYear * secondsInDay;
    subtracted = secondsInEra - subtracted;
    remainingTS = subtracted - remainingTS;

    if (remainingTS === 0) {
      month = 1;
      day = 1;
    } else {
      let remainingDays = floatToInt(remainingTS / secondsInDay);
      remainingTS -= remainingDays * secondsInDay;
      ({ month, day } = calculateMonthAndDayForDaysIntoNormalYear(calendar, remainingDays));
    }
  } else {
    let { years: yearsSinceStart, remainingDays } = calcLeapingYearsInDays(
      precalculations,
      daysSinceStart,
      eraObj.leapOffset,
      daysInLeapOffset
    );
    const subtractedDays = daysSinceStart - remainingDays;
    const subtractedSeconds = subtractedDays * secondsInDay;

    daysSinceStart = remainingDays;

    year = yearsSinceStart + 1;

    const subtracted = secondsInEra - subtractedSeconds;

    remainingTS = subtracted - remainingTS;

    if (remainingTS === 0) {
      month = 1;
      day = 1;
    } else {
      let remainingDays = floatToInt(remainingTS / secondsInDay);
      let isLeapYear = isLeapYearInEra(calendar, era, year);

      if (isLeapYear) {
        ({ month, day } = calculateMonthAndDayForDaysIntoLeapYear(calendar, remainingDays));
      } else {
        ({ month, day } = calculateMonthAndDayForDaysIntoNormalYear(calendar, remainingDays));
      }
      const subtracted = remainingDays * secondsInDay;
      remainingTS -= subtracted;
    }
  }

  if (remainingTS === 0) {
    hour = 0;
    minute = 0;
    second = 0;
  } else {
    hour = floatToInt(remainingTS / 3600);
    remainingTS -= hour * 3600;
    minute = floatToInt(remainingTS / 60);
    remainingTS -= minute * 60;
    second = remainingTS;
  }
  return { era, year, month, day, hour, minute, second };
};

const calculateComponentsForNegativeTimestamp = (calendar, timestamp) => {
  // Need to calculate components date for previous eras...
  const precalculations = calendarPrecalculations(calendar);
  const numEras = getEras(calendar).count();
  let remainingTS = -timestamp;

  // Work out which era we are in...
  let era = numEras - 2;
  let secondsInEra = precalculations.secondsInEra[era];

  while (era > 0 && remainingTS > secondsInEra) {
    remainingTS -= secondsInEra;
    era--;
    secondsInEra = precalculations.secondsInEra[era];
  }

  const eraObj = getEra(calendar, era);
  if (eraObj.get("isBackwards")) {
    return calculateComponentsForNegativeTimestampBackwardsEra(calendar, remainingTS, era);
  } else {
    return calculateComponentsForNegativeTimestampForwardsEra(calendar, remainingTS, era);
  }
};

export const testFunctions = {
  calculateComponentsForNegativeTimestampBackwardsEra
};

// const calculateDaysSinceEraStartForDateComponents = () => {};

// export default {
//   calculateTimestampForDate,
//   calculateComponentsForTimestamp,
//   calculateWeekdayForTimestamp,
//   isLeapYearInEra,
//   isGregorianCalendar
// };
